import { Button, Divider, Flex, Form, Input } from 'antd'
import { NumericFormat } from 'react-number-format'
import { tabFields } from '~app/configuration/labels'
import type {
  DocumentFragment,
  DocumentSenderFragment as DocumentSender,
} from '~app/graph/generated/graphql'
import type { AutoFocusedFields } from '~app/hooks/useField'
import i18n from '~app/i18n/i18n'
import ClearPaymentMetadataButton from './ClearPaymentMetadataButton'

type DocumentPaymentMetadata_paymentMetadata =
  DocumentFragment['paymentMetadata']

const ThousandSeparator = '.'
const DecimalSeparator = ','
const DecimalPrecision = 2

interface Props {
  documentId: string
  paymentFields: AutoFocusedFields<DocumentPaymentMetadata_paymentMetadata>
  sender: DocumentSender['senderMetadata']
}

const PaymentDetails = ({ paymentFields, documentId, sender }: Props) => {
  const { iban, reference, amount, recipient } = paymentFields

  return (
    <>
      <Divider
        orientation="left"
        orientationMargin={0}
        style={{ margin: '8px 0' }}
      >
        {i18n.t('documentView.payment')}
      </Divider>
      <Form.Item
        label={
          recipient.error || i18n.t('documentView.paymentForm.recipient.label')
        }
        validateStatus={recipient.error && 'error'}
        style={{ marginBottom: 8 }}
      >
        <Flex vertical gap={4}>
          <Input
            tabIndex={tabFields.paymentRecepient}
            {...recipient.inputField}
            placeholder={i18n.t(
              'documentView.paymentForm.recipient.placeholder'
            )}
          />
          <Button
            size="small"
            onClick={() => {
              recipient.setValue(sender.name)
              recipient.focus()
            }}
          >
            {i18n.t('documentView.fillSender')}
          </Button>
        </Flex>
      </Form.Item>
      <Form.Item
        label={iban.error || i18n.t('documentView.paymentForm.iban.label')}
        validateStatus={iban.error && 'error'}
        style={{ marginBottom: 8 }}
      >
        <Input
          tabIndex={tabFields.paymentIban}
          {...iban.inputField}
          placeholder={i18n.t('documentView.paymentForm.iban.label')}
        />
      </Form.Item>
      <Form.Item
        label={amount.error || i18n.t('documentView.paymentForm.amount.label')}
        validateStatus={amount.error && 'error'}
        style={{ marginBottom: 8 }}
      >
        <NumericFormat
          onFocus={amount.inputField.onFocus}
          onBlur={amount.inputField.onBlur}
          placeholder={i18n.t('documentView.paymentForm.amount.placeholder')}
          value={amount.value || ''}
          decimalSeparator={DecimalSeparator}
          thousandSeparator={ThousandSeparator}
          suffix="€"
          onValueChange={values => {
            if (values.floatValue === amount.value) {
              // Prevent recursive loop that leads to app-crash
              return
            }
            const amountValue = values.floatValue
            if (!amountValue || amountValue === 0) {
              amount.setValue(null)
            } else {
              amount.setValue(amountValue)
            }
          }}
          customInput={Input}
          decimalScale={DecimalPrecision}
          fixedDecimalScale
          tabIndex={tabFields.paymentAmount}
        />
      </Form.Item>
      <Form.Item
        label={
          reference.error || i18n.t('documentView.paymentForm.reference.label')
        }
        validateStatus={reference.error && 'error'}
        style={{ marginBottom: 8 }}
      >
        <Input
          tabIndex={tabFields.paymentReference}
          {...reference.inputField}
          placeholder={i18n.t('documentView.paymentForm.reference.placeholder')}
        />
      </Form.Item>
      <ClearPaymentMetadataButton
        documentId={documentId}
        onDone={() => {
          // reset field values
          iban.setValue(null)
          reference.setValue(null)
          amount.setValue(null)
          recipient.setValue(null)
        }}
      />
    </>
  )
}

export default PaymentDetails
